import React, { useState, useEffect, useRef } from "react";
import {
  DatePicker,
  TimePicker,
  LocalizationProvider,
} from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { Box } from "@mui/material";

import campaignStepOneIcon from "../../assets/images/svg/mab-compgn-8.svg";
import dropOpenIcon from "../../assets/images/svg/mab-drop-open-8.svg";
import downArrow from "../../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../../assets/images/svg/mab-ellipse.svg";
import NextButton from "../common/next-button";
import { apiBaseUrl } from "../consts/api-url.const";

// Meta design: dark calendar/dialog, white text, teal accent (#58F2A9)
const META_PICKER_PAPER_SX = {
  backgroundColor: "#1B2731",
  color: "#FFFFFF",
  borderRadius: "16px",
  border: "1px solid #2f3444",
  fontFamily: "Poppins, Helvetica",
  // Date calendar
  "& .MuiPickersCalendarHeader-root": { color: "#FFFFFF" },
  "& .MuiPickersCalendarHeader-labelContainer .MuiTypography-root": {
    color: "#FFFFFF",
    fontFamily: "Poppins, Helvetica",
  },
  "& .MuiIconButton-root": { color: "#FFFFFF" },
  "& .MuiDayCalendar-weekDayLabel": { color: "#8FA5BF" },
  "& .MuiPickersDay-root": {
    color: "#FFFFFF",
    fontFamily: "Poppins, Helvetica",
  },
  "& .MuiPickersDay-root:not(.Mui-selected):hover": {
    backgroundColor: "rgba(88, 242, 169, 0.15)",
  },
  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: "#58F2A9",
    color: "#0B0D12",
  },
  "& .MuiPickersDay-root.Mui-selected:hover": { backgroundColor: "#58F2A9" },
  // Time clock
  "& .MuiTimeClock-root": { color: "#FFFFFF" },
  "& .MuiClockNumber-root": { color: "#FFFFFF" },
  "& .MuiClockPointer-thumb": {
    backgroundColor: "#58F2A9",
    borderColor: "#58F2A9",
  },
  "& .MuiClockPointer-root": { backgroundColor: "#58F2A9" },
  // Actions (Clear, OK, Today)
  "& .MuiPickersActionBar-root .MuiButton-root": {
    color: "#58F2A9",
    fontFamily: "Poppins, Helvetica",
  },
  "& .MuiDialogActions-root .MuiButton-root": { color: "#58F2A9" },
};

const META_PICKER_FIELD_SX = {
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiInputBase-input": {
    color: "white",
    padding: "12px 16px",
    fontFamily: "Poppins, Helvetica",
  },
  "& .MuiInputBase-root": { backgroundColor: "transparent" },
};

const META_PICKER_ROOT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(135, 141, 161, 0.1)",
    borderRadius: "16px",
    "& fieldset": { border: "none" },
    fontFamily: "Poppins, Helvetica",
  },
};

// Display names used in the dropdown (must stay in sync with timeZoneToIanaMap below)
const TIMEZONE_DISPLAY_LIST = [
  "Helsinki, Finland (GMT+2)",
  "UTC (GMT+0)",
  "New York, USA (GMT-5)",
  "Los Angeles, USA (GMT-8)",
  "London, UK (GMT+1)",
] as const;

const TIMEZONE_TO_IANA: Record<string, string> = {
  "Helsinki, Finland (GMT+2)": "Europe/Helsinki",
  "UTC (GMT+0)": "UTC",
  "New York, USA (GMT-5)": "America/New_York",
  "Los Angeles, USA (GMT-8)": "America/Los_Angeles",
  "London, UK (GMT+1)": "Europe/London",
};

const IANA_TO_DISPLAY: Record<string, string> = Object.fromEntries(
  Object.entries(TIMEZONE_TO_IANA).map(([display, iana]) => [iana, display]),
);

function toDisplayTimezone(ianaOrDisplay: string | undefined): string {
  if (!ianaOrDisplay) return "Helsinki, Finland (GMT+2)";
  if (
    TIMEZONE_DISPLAY_LIST.includes(
      ianaOrDisplay as (typeof TIMEZONE_DISPLAY_LIST)[number],
    )
  )
    return ianaOrDisplay;
  return IANA_TO_DISPLAY[ianaOrDisplay] ?? "Helsinki, Finland (GMT+2)";
}

function toIanaTimezone(display: string): string {
  return TIMEZONE_TO_IANA[display] ?? "UTC";
}

type BudgetDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onNext?: (id: string) => void;
  onBudgetChange?: (budget: string) => void;
  onStartDateChange?: (startDate: string) => void;
  onEndDateChange?: (endDate: string) => void;
  onScheduleChange?: (schedule: "always" | "scheduled") => void;
  onTimezoneChange?: (timezone: string) => void;
  onStartDateTimeChange?: (date: Dayjs | null, time: string) => void;
  onEndDateTimeChange?: (date: Dayjs | null, time: string) => void;
  onCurrencyChange?: (currency: string) => void;
  isEditMode?: boolean;
  initialBudget?: string;
  initialSchedule?: "always" | "scheduled";
  initialTimezone?: string;
  initialStartDate?: Dayjs | null;
  initialEndDate?: Dayjs | null;
  initialStartTime?: string;
  initialEndTime?: string;
  initialCurrency?: string; // Currency from API response (for edit mode)
  adAccountId?: string; // Ad account ID to fetch budget info
};

const BudgetDropdown: React.FC<BudgetDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  onNext,
  onBudgetChange,
  onStartDateChange,
  onEndDateChange,
  onScheduleChange,
  onTimezoneChange,
  onStartDateTimeChange,
  onEndDateTimeChange,
  onCurrencyChange,
  isEditMode = false,
  initialBudget,
  initialSchedule,
  initialTimezone,
  initialStartDate,
  initialEndDate,
  initialStartTime,
  initialEndTime,
  initialCurrency,
  adAccountId,
}) => {
  const isOpen = expandedId === id;

  // Track if update is from parent prop (to prevent infinite loop)
  const isUpdatingFromParent = useRef(false);
  const hasFetchedApiBudget = useRef(false); // Track if API budget has been fetched

  const [dailyBudget, setDailyBudget] = useState(initialBudget ?? "");
  const [minBudget, setMinBudget] = useState<number>(0); // Will be set from API
  const [currency, setCurrency] = useState<string>(initialCurrency || ""); // Use initialCurrency if provided

  const [schedule, setSchedule] = useState<"always" | "scheduled">(
    initialSchedule || "always",
  );

  const [timeZone, setTimeZone] = useState(() =>
    toDisplayTimezone(initialTimezone),
  );
  const [startDate, setStartDate] = useState<Dayjs | null>(
    initialStartDate || null,
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(initialEndDate || null);
  const [startTime, setStartTime] = useState(initialStartTime || "00:00");
  const [endTime, setEndTime] = useState(initialEndTime || "00:00");
  const [isTimezoneOpen, setIsTimezoneOpen] = useState(false);
  const timezoneDropdownRef = useRef<HTMLDivElement>(null);

  // Close timezone dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        timezoneDropdownRef.current &&
        !timezoneDropdownRef.current.contains(e.target as Node)
      ) {
        setIsTimezoneOpen(false);
      }
    };
    if (isTimezoneOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isTimezoneOpen]);

  // Fetch budget info from API when adAccountId changes
  // This ensures minBudget and currency are always up-to-date from Meta API
  useEffect(() => {
    const fetchBudgetInfo = async () => {
   const adAccountData = localStorage.getItem("metaAdsAccountData");

let adAccountId = null;

if (adAccountData) {
  const parsedData = JSON.parse(adAccountData);
  adAccountId = parsedData?.adAccount?.id;
}

console.log(adAccountId);

      try {
        const response = await fetch(
          `${apiBaseUrl}/api/meta-builder/budget-info/${adAccountId}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );

        if (response.ok) {
          const data = await response.json();
          console.log("Budget info fetched:", data);

          // Update currency from API response
          if (data.currency) {
            console.log("Setting currency:", data.currency);
            setCurrency(data.currency);
            // Notify parent of currency change
            if (onCurrencyChange) {
              onCurrencyChange(data.currency);
            }
          }

          // Find the minimum budget from the response
          if (data.minimumBudgets && data.minimumBudgets.length > 0) {
            const minBudgetData = data.minimumBudgets[0];

            // Get min_daily_budget_high_freq (in cents, convert to dollars)
            const minDailyBudgetHighFreq =
              minBudgetData.min_daily_budget_high_freq;
            if (minDailyBudgetHighFreq) {
              // Convert from cents to dollars and format to 2 decimal places
              const minBudgetInDollars = (minDailyBudgetHighFreq / 100).toFixed(
                2,
              );
              const minBudgetValue = parseFloat(minBudgetInDollars);
              console.log("Setting minimum budget:", minBudgetInDollars);

              // Update the minimum budget state
              setMinBudget(minBudgetValue);

              // Mark that we've fetched API budget
              hasFetchedApiBudget.current = true;

              // Only set daily budget from API if it's not already set (new campaign or draft restore without budget)
              // Don't override if user has already entered a value or if editing existing campaign
              if (!dailyBudget || dailyBudget === "") {
                setDailyBudget(minBudgetInDollars);

                // Notify parent of the API budget change immediately
                if (onBudgetChange) {
                  onBudgetChange(minBudgetInDollars);
                }
              }
            }
          }
        }
      } catch (error) {
        console.error("Error fetching budget info:", error);
      }
    };

    fetchBudgetInfo();
  }, [adAccountId]);

  useEffect(() => {
    if (initialSchedule !== undefined) {
      setSchedule(initialSchedule);
    }
  }, [initialSchedule]);

  useEffect(() => {
    if (initialTimezone !== undefined) {
      setTimeZone(toDisplayTimezone(initialTimezone));
    }
  }, [initialTimezone]);

  useEffect(() => {
    if (initialStartDate !== undefined) {
      setStartDate(initialStartDate);
    }
  }, [initialStartDate]);

  useEffect(() => {
    if (initialEndDate !== undefined) {
      setEndDate(initialEndDate);
    }
  }, [initialEndDate]);

  useEffect(() => {
    if (initialStartTime !== undefined) {
      setStartTime(initialStartTime);
    }
  }, [initialStartTime]);

  useEffect(() => {
    if (initialEndTime !== undefined) {
      setEndTime(initialEndTime);
    }
  }, [initialEndTime]);

  // Update currency when initialCurrency prop changes (for edit mode)
  useEffect(() => {
    if (initialCurrency !== undefined) {
      setCurrency(initialCurrency);
      // Notify parent of currency change
      if (onCurrencyChange) {
        onCurrencyChange(initialCurrency);
      }
    }
  }, [initialCurrency, onCurrencyChange]);

  useEffect(() => {
    // Only apply initialBudget if we haven't fetched from API yet AND we haven't already set a value
    // This ensures API value takes priority over default value
    // Also prevents resetting when user has already entered a value
    if (
      initialBudget !== undefined &&
      !hasFetchedApiBudget.current &&
      dailyBudget === ""
    ) {
      isUpdatingFromParent.current = true;
      setDailyBudget(initialBudget);
      // Reset flag after state update
      setTimeout(() => {
        isUpdatingFromParent.current = false;
      }, 0);
    }
  }, [initialBudget]);

  useEffect(() => {
    // Only notify parent if update is NOT from parent prop (user input)
    if (onBudgetChange && !isUpdatingFromParent.current) {
      onBudgetChange(dailyBudget);
    }
  }, [dailyBudget, onBudgetChange]);

  const timeZones = TIMEZONE_DISPLAY_LIST;

  // Format date from Dayjs to DD/MM/YYYY
  const formatDate = (date: Dayjs | null): string => {
    if (!date) return "";
    return date.format("DD/MM/YYYY");
  };

  useEffect(() => {
    if (onScheduleChange) {
      onScheduleChange(schedule);
    }
  }, [schedule, onScheduleChange]);

  useEffect(() => {
    if (onTimezoneChange) {
      // Send IANA timezone format instead of display format
      onTimezoneChange(toIanaTimezone(timeZone));
    }
  }, [timeZone, onTimezoneChange]);

  useEffect(() => {
    if (onStartDateTimeChange) {
      onStartDateTimeChange(startDate, startTime);
    }
  }, [startDate, startTime, onStartDateTimeChange]);

  useEffect(() => {
    if (onEndDateTimeChange) {
      onEndDateTimeChange(endDate, endTime);
    }
  }, [endDate, endTime, onEndDateTimeChange]);

  useEffect(() => {
    if (onStartDateChange) {
      if (schedule === "always") {
        onStartDateChange("None (run when published)");
      } else if (schedule === "scheduled") {
        if (startDate) {
          onStartDateChange(formatDate(startDate));
        } else {
          onStartDateChange("None (run when published)");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule, startDate, onStartDateChange]);

  useEffect(() => {
    if (onEndDateChange) {
      if (schedule === "always") {
        onEndDateChange("None (run until turned off manually)");
      } else if (schedule === "scheduled") {
        if (endDate) {
          onEndDateChange(formatDate(endDate));
        } else {
          onEndDateChange("None (run until turned off manually)");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule, endDate, onEndDateChange]);

  return (
    <div className="w-full">
      <div
        className={`relative rounded-[12px] sm:rounded-[16px] md:rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] w-full ${
          schedule === "scheduled" ? "overflow-visible" : "overflow-hidden"
        }`}
        style={{
          background: "#878DA11A",
          backgroundImage: `url(${ellipseBg})`,
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-90 transition-opacity min-h-[60px] h-[60px] sm:min-h-[72px] sm:h-[72px] md:min-h-[88px] md:h-[88px] px-4 py-4 sm:px-4 sm:py-4 md:px-6 md:py-6"
          onClick={() => onToggle(id)}
        >
          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
            <img
              src={isOpen ? dropOpenIcon : campaignStepOneIcon}
              alt="Budget"
              className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0"
            />
            <span
              className="text-sm md:text-[20px] whitespace-nowrap"
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                color: isOpen ? "#58F2A9" : "#FFFFFF",
              }}
            >
              Budget
            </span>
          </div>

          <img
            src={downArrow}
            alt="Toggle"
            className="pointer-events-none transition-transform flex-shrink-0"
            style={{
              height: "9.5px",
              width: "auto",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              filter: isOpen
                ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
                : "none",
            }}
          />
        </div>

        {isOpen && (
          <div className="px-4 pb-4 sm:px-4 sm:pb-4 md:px-6 md:pb-6">
            <div className="flex flex-col gap-3 md:gap-6 md:ml-[41px]">
              {/* Budget */}
              <div className="flex flex-col gap-3">
                <h3 className="text-white font-medium text-[14px] lg:text-[16px]">
                  Choose your Daily Budget Amount
                </h3>

                <div className="flex items-center gap-3 md:gap-4 bg-white rounded-[12px] text-[#020202] min-h-[56px] md:min-h-[64px] px-3 py-3 md:px-[18px] md:py-4">
                  <span className="text-xs md:text-[14px] font-semibold text-[#8A8A8A] flex-shrink-0">
                    {currency}
                  </span>

                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder={minBudget.toFixed(2)}
                    value={dailyBudget}
                    onChange={(e) => {
                      const value = e.target.value;

                      // allow numbers + decimal only
                      if (!/^\d*\.?\d*$/.test(value)) return;

                      setDailyBudget(value);
                    }}
                    onBlur={() => {
                      const num = parseFloat(dailyBudget);
                      if (isNaN(num) || num < minBudget) {
                        setDailyBudget(minBudget.toFixed(2));
                      } else {
                        setDailyBudget(num.toFixed(2));
                      }
                    }}
                    className="flex-1 min-w-0 bg-transparent outline-none text-sm md:text-[14px] font-semibold"
                  />
                </div>

                <p className="text-[#8FA5BF] font-medium text-[12px] lg:text-[16px]">
                  Your budget needs to be {minBudget} {currency} or greater or
                  your ads may not deliver.
                </p>
              </div>

              {/* Schedule */}
              <div className="flex flex-col gap-3">
                <h4 className="text-white font-medium text-[14px] lg:text-[16px]">
                  Select when this Ad will be running
                </h4>

                <div className="flex flex-col gap-4">
                  <button
                    type="button"
                    onClick={() => !isEditMode && setSchedule("always")}
                    disabled={isEditMode}
                    className="flex items-center gap-3 text-white text-sm md:text-[16px]"
                    style={{
                      opacity: isEditMode ? 0.6 : 1,
                      cursor: isEditMode ? "not-allowed" : "pointer",
                    }}
                  >
                    <span
                      className={`inline-flex h-5 w-5 rounded-full border-2 ${
                        schedule === "always"
                          ? "border-[#58F2A9] bg-[#58F2A9]"
                          : "border-[#878DA199]"
                      }`}
                    />
                    Always Active
                  </button>

                  <button
                    type="button"
                    onClick={() => !isEditMode && setSchedule("scheduled")}
                    disabled={isEditMode}
                    className="flex items-center gap-3 text-white text-[14px] lg:text-[16px]"
                    style={{
                      opacity: isEditMode ? 0.6 : 1,
                      cursor: isEditMode ? "not-allowed" : "pointer",
                    }}
                  >
                    <span
                      className={`inline-flex h-5 w-5 rounded-full border-2 ${
                        schedule === "scheduled"
                          ? "border-[#58F2A9] bg-[#58F2A9]"
                          : "border-[#878DA199]"
                      }`}
                    />
                    Scheduled
                  </button>
                </div>
              </div>

              {/* Scheduled section remains UNCHANGED */}
              {schedule === "scheduled" && (
                <div className="flex flex-col gap-6">
                  {/* your existing scheduled UI exactly as-is */}
                </div>
              )}

              {/* Schedule Section */}
              {schedule === "scheduled" && (
                <div className="flex flex-col gap-4 md:gap-6">
                  <div
                    className="flex flex-col gap-2"
                    ref={timezoneDropdownRef}
                  >
                    <span className="text-white text-[14px] lg:text-[16px] font-medium [font-family:'Poppins',Helvetica]">
                      Time Zone
                    </span>

                    <div
                      className="relative w-full max-w-[340px] min-w-0"
                      style={{ opacity: isEditMode ? 0.6 : 1 }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          !isEditMode && setIsTimezoneOpen((v) => !v)
                        }
                        disabled={isEditMode}
                        className="w-full h-[50px] md:h-[56px] rounded-full flex items-center justify-between bg-gradient-to-b from-[#878DA11A] to-[#878DA11A] px-4 md:px-6 text-left text-white text-sm md:text-[16px] outline-none border border-transparent hover:border-[#2f3444] transition-colors [font-family:'Poppins',Helvetica] disabled:cursor-not-allowed"
                      >
                        <span className="truncate pr-2">{timeZone}</span>
                        <svg
                          className={`w-5 h-5 flex-shrink-0 text-white transition-transform ${isTimezoneOpen ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>

                      {/* Meta-styled dropdown list - stays in viewport */}
                      {isTimezoneOpen && (
                        <div
                          className="absolute left-0 right-0 z-[100] mt-1 max-h-[240px] overflow-auto rounded-[16px] border border-[#2f3444] bg-[#1B2731] py-1 shadow-lg"
                          style={{ top: "100%" }}
                        >
                          {timeZones.map((tz) => (
                            <button
                              key={tz}
                              type="button"
                              onClick={() => {
                                setTimeZone(tz);
                                setIsTimezoneOpen(false);
                              }}
                              className={`w-full px-4 py-3 text-left text-sm md:text-[16px] [font-family:'Poppins',Helvetica] transition-colors ${
                                timeZone === tz
                                  ? "bg-[#58F2A9]/20 text-[#58F2A9]"
                                  : "text-white hover:bg-[#2f3444]"
                              }`}
                            >
                              {tz}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Titles - Hide on mobile */}
                  <div className="hidden sm:flex sm:flex-row sm:items-center gap-2 sm:gap-6">
                    <span className="text-white text-sm md:text-[16px] font-medium sm:w-[180px]">
                      Start Date & Time
                    </span>
                    <span className="text-white text-sm md:text-[16px] font-medium sm:w-[180px]">
                      End Date & Time
                    </span>
                  </div>

                  {/* Date Row - MUI DatePicker opens in dialog on mobile so it stays on screen */}
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mt-2">
                      {/* Start Date */}
                      <div className="flex flex-col gap-2 w-full sm:w-[180px]">
                        <span className="sm:hidden text-white text-[14px] font-medium">
                          Start Date
                        </span>
                        <DatePicker
                          value={startDate}
                          onChange={(v) => setStartDate(v)}
                          disabled={isEditMode}
                          desktopModeMediaQuery="(min-width: 900px)"
                          slotProps={{
                            popper: {
                              placement: "bottom-start",
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  options: { padding: 8 },
                                },
                                { name: "flip", options: { padding: 8 } },
                              ],
                            },
                            field: { sx: META_PICKER_FIELD_SX },
                            desktopPaper: { sx: META_PICKER_PAPER_SX },
                            mobilePaper: { sx: META_PICKER_PAPER_SX } as any,
                            openPickerButton: { sx: { color: "#FFFFFF" } },
                          }}
                          sx={META_PICKER_ROOT_SX}
                        />
                      </div>

                      {/* End Date */}
                      <div className="flex flex-col gap-2 w-full sm:w-[180px]">
                        <span className="sm:hidden text-white text-[14px] font-medium">
                          End Date
                        </span>
                        <DatePicker
                          value={endDate}
                          onChange={(v) => setEndDate(v)}
                          desktopModeMediaQuery="(min-width: 900px)"
                          slotProps={{
                            popper: {
                              placement: "bottom-start",
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  options: { padding: 8 },
                                },
                                { name: "flip", options: { padding: 8 } },
                              ],
                            },
                            field: { sx: META_PICKER_FIELD_SX },
                            desktopPaper: { sx: META_PICKER_PAPER_SX },
                            mobilePaper: { sx: META_PICKER_PAPER_SX } as any,
                            openPickerButton: { sx: { color: "#FFFFFF" } },
                          }}
                          sx={META_PICKER_ROOT_SX}
                        />
                      </div>
                    </div>

                    {/* Time Row - MUI TimePicker opens in dialog on mobile so it stays on screen */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mt-3">
                      {/* Start Time */}
                      <div className="flex flex-col gap-2 w-full sm:w-[180px]">
                        <span className="sm:hidden text-white text-[14px] font-medium">
                          Start Time
                        </span>
                        <TimePicker
                          value={
                            startTime ? dayjs(`1970-01-01T${startTime}`) : null
                          }
                          onChange={(v) =>
                            setStartTime(v ? v.format("HH:mm") : "00:00")
                          }
                          disabled={isEditMode}
                          desktopModeMediaQuery="(min-width: 900px)"
                          slotProps={{
                            popper: {
                              placement: "bottom-start",
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  options: { padding: 8 },
                                },
                                { name: "flip", options: { padding: 8 } },
                              ],
                            },
                            field: { sx: META_PICKER_FIELD_SX },
                            desktopPaper: { sx: META_PICKER_PAPER_SX },
                            mobilePaper: { sx: META_PICKER_PAPER_SX } as any,
                            openPickerButton: { sx: { color: "#FFFFFF" } },
                          }}
                          sx={META_PICKER_ROOT_SX}
                        />
                      </div>

                      {/* End Time */}
                      <div className="flex flex-col gap-2 w-full sm:w-[180px]">
                        <span className="sm:hidden text-white text-[14px] font-medium">
                          End Time
                        </span>
                        <TimePicker
                          value={
                            endTime ? dayjs(`1970-01-01T${endTime}`) : null
                          }
                          onChange={(v) =>
                            setEndTime(v ? v.format("HH:mm") : "00:00")
                          }
                          desktopModeMediaQuery="(min-width: 900px)"
                          slotProps={{
                            popper: {
                              placement: "bottom-start",
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  options: { padding: 8 },
                                },
                                { name: "flip", options: { padding: 8 } },
                              ],
                            },
                            field: { sx: META_PICKER_FIELD_SX },
                            desktopPaper: { sx: META_PICKER_PAPER_SX },
                            mobilePaper: { sx: META_PICKER_PAPER_SX } as any,
                            openPickerButton: { sx: { color: "#FFFFFF" } },
                          }}
                          sx={META_PICKER_ROOT_SX}
                        />
                      </div>
                    </div>
                  </LocalizationProvider>
                </div>
              )}
              {/* Next button */}
              <div className="flex w-full justify-end">
                <NextButton
                  size="sm"
                  className="w-full md:w-auto"
                  onClick={() => onNext?.(id)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetDropdown;
